import useGetMyAccountSubdomain from '@/api/queries/me/useGetMyAccountSubdomain';
import { GetProjectEstimatesQuery } from '@/gql/graphql';
import { Button, Tooltip } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { type Contact } from './contacts';
import EstimateHeaderIdentity from './EstimateHeaderIdentity';
import EstimateRecipientPicker from './EstimateRecipientPicker';

type Estimate = GetProjectEstimatesQuery['projectById']['estimates'][number];

const getSendDisabledReason = (params: {
  hasActiveSubdomain: boolean;
  hasPricedLineItem: boolean;
}): string | null => {
  const { hasActiveSubdomain, hasPricedLineItem } = params;
  if (!hasActiveSubdomain) {
    return 'Client Portal access is required to send Estimates. Set up your Client Portal subdomain in account settings.';
  }
  if (!hasPricedLineItem) {
    return 'Add at least one line item with a price before sending.';
  }
  return null;
};

type EstimateBuilderSidebarHeaderProps = {
  projectId: string;
  estimateId: string;
  estimate: Estimate | undefined;
  contacts: Contact[];
  recipient: Contact | null;
  onSelectRecipient: (contact: Contact) => void;
  onEnterSendMode: () => void;
};

const EstimateBuilderSidebarHeader: FC<EstimateBuilderSidebarHeaderProps> = (
  props
) => {
  const {
    projectId,
    estimateId,
    estimate,
    contacts,
    recipient,
    onSelectRecipient,
    onEnterSendMode,
  } = props;

  const { data: subdomain } = useGetMyAccountSubdomain();

  const isSent = estimate?.status === 'SENT';
  const isArchived = estimate?.status === 'ARCHIVED';
  const hasActiveSubdomain = subdomain?.isActive === true;
  const hasPricedLineItem =
    estimate?.sections.some((section) =>
      section.lineItems.some((item) => item.unitPriceExcTax.value > 0)
    ) ?? false;
  const sendDisabledReason = getSendDisabledReason({
    hasActiveSubdomain,
    hasPricedLineItem,
  });
  const sendDisabled = sendDisabledReason !== null;
  const showPreviewButton = estimate?.status === 'DRAFT';

  return (
    <div className="border-b border-gray-200 typography:prose p-3.5">
      <div className="flex items-center justify-between flex-wrap gap-x-8 gap-y-2">
        <EstimateHeaderIdentity projectId={projectId} estimateId={estimateId} />

        {!isArchived && showPreviewButton && (
          <Tooltip
            tooltipContent={sendDisabledReason ?? ''}
            enabled={sendDisabled}
          >
            <Button
              variant="solid"
              size="sm"
              disabled={sendDisabled}
              onClick={onEnterSendMode}
            >
              <UntitledIcon name="send-01.3" className="size-4" />
              Draft & send
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default EstimateBuilderSidebarHeader;
